import type { Plan } from '@obv/api-client';
import { EVERYONE, FAMILY, addDays, addMonths, buildAgenda, dayLabel, daysBetween, formatTime, monthLabel, nextUp, planCountLabel, plansForDay, weekDays, weekLabel } from '@obv/core';
import { personColor } from '@obv/ui';
import { useRef } from 'react';
import { useLocation } from 'wouter';
import { newMutationKey, usePlanWindow, useUpdatePlan } from '@/queries/hooks';
import { memberLabel, useDisplay } from '../context';
import { MAX_OFFSET, MIN_OFFSET, useDisplayUi, type CalendarView } from '../store';
import { Avatar, Button, Card, Chip, ScreenHeader, Segmented, Unavailable } from '../ui';
import { MonthView, WeekView } from './CalendarGrid';

const LONG_PRESS_MS = 550;

const VIEWS: { key: CalendarView; label: string }[] = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
];

export function CalendarScreen() {
  const ctx = useDisplay();
  const [, navigate] = useLocation();
  const { filter, setFilter, dayOffset, shiftDay, setDayOffset, resetDay, openSheet, calView, setCalView } = useDisplayUi();
  const today = ctx.clock.dateKey;
  const day = addDays(today, dayOffset);
  const isToday = dayOffset === 0;
  const windowQ = usePlanWindow(today, ctx.boot.server_time);
  const update = useUpdatePlan();
  const portrait = ctx.orientation === 'portrait';

  const dayPlans = windowQ.data ? plansForDay(windowQ.data.items, day, filter) : [];
  const agenda = buildAgenda(dayPlans, { isToday, now: ctx.clock.hhmm });
  const next = isToday ? nextUp(dayPlans, ctx.clock.hhmm) : undefined;
  const done = dayPlans.filter((p) => p.completed).length;

  const chips = [
    { id: EVERYONE, label: 'Everyone' },
    ...ctx.boot.members.map((m) => ({ id: m.id, label: m.display_name })),
    { id: FAMILY, label: 'Family' },
  ];

  const toggle = (p: Plan) => {
    if (p.read_only || p.id.indexOf('tmp-') === 0) return;
    update.mutate({ id: p.id, body: { completed: !p.completed }, key: newMutationKey() });
  };

  const step = (dir: 1 | -1) => {
    if (calView === 'day') shiftDay(dir);
    else if (calView === 'week') shiftDay(7 * dir);
    else setDayOffset(daysBetween(today, addMonths(day, dir)));
  };
  const openDay = (d: string) => {
    setDayOffset(daysBetween(today, d));
    setCalView('day');
  };

  return (
    <>
      <ScreenHeader
        eyebrow="Your family, in sync"
        title="A little more together."
        right={
          <div className="flex items-center">
            <div className="mr-3 w-[216px]">
              <Segmented<CalendarView> label="Calendar view" options={VIEWS} value={calView} onChange={setCalView} />
            </div>
            <Button variant="primary" onClick={() => openSheet({ mode: 'add' })}>
              + Add a plan
            </Button>
          </div>
        }
      />
      <div className="mt-4 flex flex-none flex-wrap items-center justify-between">
        <div className="-mb-2 flex flex-wrap">
          {chips.map((c) => (
            <div key={c.id} className="mb-2 mr-2">
              <Chip selected={filter === c.id} onClick={() => setFilter(c.id)}>
                {c.label}
              </Chip>
            </div>
          ))}
        </div>
        <div className="flex items-center">
          <NavArrow label={`Previous ${calView}`} disabled={dayOffset <= MIN_OFFSET} onClick={() => step(-1)}>
            ‹
          </NavArrow>
          <button type="button" className="tap mx-[6px] h-[44px] w-[140px] text-center text-[14px] font-medium" onClick={resetDay} aria-label="Back to today">
            {calView === 'day' ? dayLabel(day, today) : calView === 'week' ? weekLabel(weekDays(day, ctx.prefs.week_starts)) : monthLabel(day)}
          </button>
          <NavArrow label={`Next ${calView}`} disabled={dayOffset >= MAX_OFFSET} onClick={() => step(1)}>
            ›
          </NavArrow>
        </div>
      </div>

      {calView === 'week' ? (
        <WeekView anchor={day} plans={windowQ.data ? windowQ.data.items : []} loading={windowQ.isLoading} onOpenDay={openDay} />
      ) : calView === 'month' ? (
        <MonthView anchor={day} plans={windowQ.data ? windowQ.data.items : []} loading={windowQ.isLoading} onOpenDay={openDay} />
      ) : (
      <div
        className="mt-[14px] grid min-h-0 flex-1"
        style={{
          gridTemplateColumns: portrait ? 'minmax(0,1fr)' : 'minmax(0,1fr) 250px',
          gridTemplateRows: portrait ? 'minmax(0,1fr) auto' : 'minmax(0,1fr)',
          gridGap: 14,
        }}
      >
        <Card tone={5} className="flex flex-col overflow-hidden">
          <div className="scroll-y flex-1 px-5 pb-[10px] pt-[6px]">
            {windowQ.isLoading ? (
              <Unavailable>Loading plans…</Unavailable>
            ) : windowQ.isError && !windowQ.data ? (
              <div className="flex h-full flex-col items-center justify-center text-center">
                <div className="text-[15px]">Couldn’t load plans.</div>
                <Button className="mt-3" onClick={() => windowQ.refetch()}>
                  Try again
                </Button>
              </div>
            ) : agenda.length === 0 ? (
              <div className="flex h-full min-h-[300px] flex-col items-center justify-center text-center">
                <div className="font-serif text-[24px]">Nothing planned yet.</div>
                <div className="mt-2 text-[14px] text-muted">An open day. Add a plan if something comes up.</div>
              </div>
            ) : (
              agenda.map((item, i) => {
                if (item.kind === 'now') return <NowLine key={`now-${i}`} />;
                if (item.kind === 'allDayHeader')
                  return (
                    <div key="allday" className="pb-1 pt-3 text-[12px] font-semibold tracking-[0.08em] text-muted">
                      ALL DAY
                    </div>
                  );
                return (
                  <PlanRow
                    key={item.plan.id}
                    plan={item.plan}
                    past={item.past}
                    onToggle={() => toggle(item.plan)}
                    onEdit={() => !item.plan.read_only && item.plan.id.indexOf('tmp-') !== 0 && openSheet({ mode: 'edit', planId: item.plan.id })}
                  />
                );
              })
            )}
          </div>
        </Card>

        <div className="flex min-h-0" style={{ flexDirection: portrait ? 'row' : 'column' }}>
          <Card tone={6} className="px-5 py-[18px]" style={{ flex: portrait ? 1 : 'none' }}>
            <div className="text-[12px] font-semibold tracking-eyebrow text-muted">AT A GLANCE</div>
            <div className="mt-[6px] font-serif text-[30px]">{planCountLabel(dayPlans.length)}</div>
            <div className="mt-[2px] text-[13px] text-muted">
              {dayPlans.length ? `${done} done · tap a plan to check it off` : 'Nothing on the calendar'}
            </div>
            {next ? (
              <div className="mt-[14px] border-t border-line pt-3">
                <div className="text-[12px] text-muted">Next up</div>
                <div className="ellipsis mt-[3px] text-[16px]">{next.title}</div>
                <div className="mt-[2px] text-[13px] text-muted2">
                  {formatTime(next.time!, ctx.prefs.h24)} · {memberLabel(ctx, next.member_id).name}
                </div>
              </div>
            ) : null}
          </Card>
          <Card
            tone={7}
            className="flex flex-1 flex-col px-5 py-[18px]"
            style={portrait ? { marginLeft: 14 } : { marginTop: 14 }}
          >
            <div className="text-[16px]">Shared calendars</div>
            <div className="mt-[3px] text-[12px] text-muted">Link them from your phone</div>
            <div className="mt-[10px]">
              {['Google', 'iCloud', 'Outlook', 'Yahoo'].map((p) => (
                <div key={p} className="flex h-[34px] items-center justify-between border-t text-[14px]" style={{ borderColor: 'var(--line2)' }}>
                  <div>{p}</div>
                  <div className="text-[12px] text-muted">Not linked</div>
                </div>
              ))}
            </div>
            <button type="button" className="tap mt-auto h-[44px] self-start text-[13px] font-medium" onClick={() => navigate('/display/settings')}>
              Manage ↗
            </button>
          </Card>
        </div>
      </div>
      )}
    </>
  );
}

function NavArrow({ children, label, onClick, disabled }: { children: string; label: string; onClick: () => void; disabled: boolean }) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="tap flex h-[44px] w-[44px] items-center justify-center rounded-[10px] border border-bd text-[18px] disabled:opacity-30"
    >
      {children}
    </button>
  );
}

function NowLine() {
  return (
    <div className="flex h-[22px] items-center" aria-label="Now">
      <div className="w-[62px] text-[12px] font-bold tracking-[0.06em]" style={{ color: 'var(--now)' }}>
        NOW
      </div>
      <div className="-ml-1 h-2 w-2 rounded-full" style={{ background: 'var(--now)' }} />
      <div className="h-[2px] flex-1" style={{ background: 'var(--now)' }} />
    </div>
  );
}

function PlanRow({ plan, past, onToggle, onEdit }: { plan: Plan; past: boolean; onToggle: () => void; onEdit: () => void }) {
  const ctx = useDisplay();
  const who = memberLabel(ctx, plan.member_id);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressed = useRef(false);
  const start = () => {
    longPressed.current = false;
    timer.current = setTimeout(() => {
      longPressed.current = true;
      onEdit();
    }, LONG_PRESS_MS);
  };
  const cancel = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  const opacity = plan.completed ? 0.5 : past ? 0.55 : 1;
  return (
    <div className="flex h-[56px] items-center border-b" style={{ borderColor: 'var(--line2)', opacity }}>
      <button
        type="button"
        className="tap flex h-full min-w-0 flex-1 items-center text-left"
        onTouchStart={start}
        onTouchEnd={cancel}
        onTouchMove={cancel}
        onMouseDown={start}
        onMouseUp={cancel}
        onMouseLeave={cancel}
        onClick={() => {
          if (!longPressed.current) onToggle();
        }}
        onContextMenu={(e) => e.preventDefault()}
        aria-label={`${plan.title}${plan.completed ? ', done' : ''}. Tap to ${plan.completed ? 'undo' : 'check off'}, hold to edit.`}
      >
        <span className="tabular w-[62px] flex-none text-[13px] text-muted">{plan.all_day ? 'All day' : formatTime(plan.time!, ctx.prefs.h24)}</span>
        <span className="mx-[14px] h-[26px] w-1 flex-none rounded-[2px]" style={{ background: personColor(who.colorKey) }} />
        <span className="ellipsis min-w-0 flex-1 text-[16px]" style={{ textDecoration: plan.completed ? 'line-through' : 'none' }}>
          {plan.title}
        </span>
        <span className="ml-3 flex items-center text-[12px] text-muted2">
          <Avatar initial={who.initial} />
          <span className="ml-[7px]">{who.name}</span>
        </span>
        {plan.read_only ? (
          <span className="ml-[14px] text-[11px] uppercase tracking-[0.08em] text-muted">{plan.source}</span>
        ) : (
          <span
            className="ml-[14px] flex h-[24px] w-[24px] flex-none items-center justify-center rounded-full text-[12px]"
            style={{ border: '1.5px solid var(--ink)', background: plan.completed ? 'var(--ink)' : 'transparent', color: 'var(--onInk)' }}
            aria-hidden
          >
            {plan.completed ? '✓' : ''}
          </span>
        )}
      </button>
    </div>
  );
}
